import { Button } from '@/shared/ui/button.tsx'
import { Form, FormMessage } from '@/shared/ui/form.tsx'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import InputWithLabel from '@/shared/inputs/InputWithLabel'
import { useAuth, useRefreshToken } from './api'
import { useContext, useEffect } from 'react'
import { AuthContext, AuthContextType } from '../app-layout/context'
import { Link, Navigate } from 'react-router-dom'
import { Card } from '@/shared/ui/card.tsx'
import { useAtomValue } from 'jotai'
import { themeAtom } from '../app-layout/atoms/theme-atoms'
import { Loader2 } from 'lucide-react'

const loginSchema = z.object({
  username: z.string().min(1, { message: "Username is required" }),
  password: z.string().min(1, { message: "Password is required" })
})

export type LoginSchemaType = z.infer<typeof loginSchema>

const AuthView = () => {

  const themeClass = useAtomValue(themeAtom)

  const { token, username, setPlaygorund } = useContext<AuthContextType>(AuthContext)

  const { refreshAccessToken, isLoading } = useRefreshToken()

  useEffect(() => {
    if (!token || !username) {
      refreshAccessToken();
    }
  }, [token, username]);


  const url = window.location.href

  const { loginUser, status, error } = useAuth()

  const form = useForm<LoginSchemaType>({
    defaultValues: {
      username: "",
      password: "",
    },
    resolver: zodResolver(loginSchema)
  })

  if (token && username) {
    return <Navigate to="/home" />
  }

  const submitHandler = (formData: LoginSchemaType) => {
    setPlaygorund(false)
    loginUser(formData)
    form.reset()
  }

  return (
    <div className='w-full h-[100vh] relative bg-top bg-cover max-sm:bg-[url(https://utfs.io/f/A17KOoeuO4h9HJY1v5NMor0Nh4Jfi5AVDOw6lmEq82F9dasj)] sm:bg-gray-100 flex p-10 gap-4 items-center justify-evenly'>
      {url.includes("deko") && (
        <Card className='absolute bottom-4 shadow-lg animate-in slide-out-to-left duration-700 left-4 w-[24rem] px-4 py-2 flex items-center justify-between gap-2'>
          <p className='font-poppins text-sm font-medium'>Check out our siAi playground</p>
          <Link to="/?play=true">
            <Button className='h-7'>Check now</Button>
          </Link>
        </Card>
      )}
      <div className='w-full sm:w-1/2 h-full flex flex-col items-center justify-center gap-3'>
        <div id="card" className='bg-white max-sm:bg-white/85 h-fit w-full lg:w-2/3 rounded-2xl md:rounded-xl shadow-lg flex flex-col items-center p-3 md:p-4 gap-2'>
          <p className='font-header text-lg w-full text-center pt-3'>Welcome to</p>
          <img src={themeClass.logo} className={themeClass.company === "SiAi" ? "w-32 h-fit" : "w-48 h-fit"} />
          <Form {...form}>
            <form onSubmit={form.handleSubmit(submitHandler)} className='flex flex-col w-full px-1 md:px-5 lg:px-10 pb-5 gap-5'>
              <InputWithLabel<LoginSchemaType> nameInSchema="username" fieldTitle="Username" />
              <InputWithLabel<LoginSchemaType> nameInSchema="password" type='password' fieldTitle="Password" />
              <Button 
                role='button'
                className='rounded-xl font-montserrat' 
                type='submit' 
                disabled={( status === "pending" || isLoading )}>
                {( status === "pending" || isLoading ) 
                  ? <>
                    <Loader2 className='animate-spin' />
                    <span className='sr-only'>Log In</span>
                  </> 
                  : "Log In"
                }</Button>
              {error ?
                <FormMessage role="status">Invalid Credentials</FormMessage>
              : null}
            </form>
          </Form>
        </div>
      </div>
      <div className='w-1/2 hidden sm:block h-full bg-blue-50 bg-center bg-cover bg-[url(https://86v8u6agau.ufs.sh/f/A17KOoeuO4h9qVq6QUku9vzH86eMobgsyNXrncWJE1DTaxmh)] rounded-3xl'></div>
    </div>
  )
}

export default AuthView
